// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import Clickable from "@insite/mobius/Clickable";
import Link, { LinkProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage, { LazyImageProps } from "@synd/MobiusOverrides/LazyImage";
import { ImageSettingKey } from "@synd/XDL/types";
import { mergeWithCss } from "@synd/XDL/Utilities/mergeWithCss";

import React, { FC, useState } from "react";

interface OwnProps {
    category: Category;
    showImage?: boolean;
    extendedStyles?: CategoryCardStyles;
    boldTitle?: boolean;
}

type Props = OwnProps;

export interface CategoryCardStyles {
    categoryWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    title?: LinkProps;
}

export const categoryCardStyles: CategoryCardStyles = {};

const CategoryCard: FC<Props> = ({ category, showImage = true, extendedStyles, boldTitle }) => {
    const [styles] = useState(() => mergeWithCss(categoryCardStyles, extendedStyles));

    if (!category) {
        return null;
    }

    return (
        <StyledWrapper key={category.id} {...styles.categoryWrapper}>
            {showImage && (
                <Clickable
                    href={category.path || (category as any).url}
                    {...styles.imageWrapper}
                    aria-label="category image clickable"
                >
                    <LazyImage
                        src={category.smallImagePath}
                        altText={category.shortDescription}
                        height="100%"
                        width="100%"
                        imageKey={ImageSettingKey.category}
                        {...styles.image}
                    />
                </Clickable>
            )}
            <Link
                aria-label={category.name}
                href={category?.path || (category as any).url}
                {...styles.title}
                {...{ boldTitle }}
            >
                {category.shortDescription}
            </Link>
        </StyledWrapper>
    );
};

export default CategoryCard;
